import React from "react";
import textSliderStyles from "./slider.module.css";
import {
  Dispatch,
  SetStateAction,
  TouchEvent,
  useCallback,
  useState,
} from "react";
import myDayStyles from "../myDay/myDay.module.css";
import { IAppointment, IUpdate, IVideo } from "../../types/types";
import Video from "../video/video";
import { formatAppointmentDate, replaceVariables } from "../../utils/utils";
import Modal from "../modals/modal/Modal";
import AppointmentCard from "../modals/appointmentCard/appointmentCard";
import VideoModalContent from "../modals/videoModal/videoModal";
import VideoThumbnail from "../videoThumbnail/videoThumbnail";
import { useAppSelector } from "../../services/hooks";
import { userSelector } from "../../services/selectors/user.selectors";
import { tWithKeyAttr as t } from "../../i18nHelper";
import i18n from "../../i18n";
import { TExercise } from "../../services/types/auth.types";

// Parse markdown [text](url) links into clickable <a> tags
const parseLinksInText = (text: string): React.ReactNode => {
  if (!text) return text;
  
  const linkRegex = /\[([^\]]+)\]\(([^)]+)\)/g;
  const parts: React.ReactNode[] = [];
  let lastIndex = 0;
  let match;

  while ((match = linkRegex.exec(text)) !== null) {
    // Add text before the link
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }
    // Add the link
    const linkText = match[1];
    const linkUrl = match[2];
    parts.push(
      <a
        key={match.index}
        href={linkUrl}
        target="_blank"
        rel="noopener noreferrer"
        className={textSliderStyles.link}
      >
        {linkText}
      </a>
    );
    lastIndex = match.index + match[0].length;
  }

  // Add remaining text after last link
  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }

  return parts.length > 0 ? parts : text;
};

function Slider({
  items,
  className,
  activeIndex,
  setActiveIndex,
  itemsPerSlide = 1, // number of items per slide
}: {
  items:
    | string[]
    | { title: string; desc: string }[]
    | { id: number; text: string; date: Date }[]
    | IUpdate[]
    | IVideo[]
    | IAppointment[];
  className: string;
  activeIndex: number;
  setActiveIndex: Dispatch<SetStateAction<number>>;
  itemsPerSlide?: number;
}) {
  const user = useAppSelector(userSelector);

  const [touchStartX, setTouchStartX] = useState<number | null>(null);
  const [openModal, setOpenModal] = useState<boolean>(false);
  const [currentAppointment, setCurrentAppointment] =
    useState<IAppointment | null>(null);
  const [openVideoModal, setOpenVideoModal] = useState<boolean>(false);
  const [currentExercise, setCurrentExercise] = useState<TExercise | null>(null);

  const openExerciseModal = (exercise: TExercise) => {
    setCurrentExercise(exercise);
    setOpenVideoModal(true);
  };

  const handlePrev = useCallback(() => {
    setActiveIndex((prevIndex: number) =>
      prevIndex === 0 ? items.length - 1 : prevIndex - 1
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items.length]);

  const handleNext = useCallback(() => {
    setActiveIndex((prevIndex) =>
      prevIndex === items.length - 1 ? 0 : prevIndex + 1
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items.length]);

  const handleTouchStart = (event: TouchEvent) => {
    if (touchStartX) return;
    setTouchStartX(event.touches[0].clientX);
  };
  const handleTouchEnd = (event: TouchEvent) => {
    if (touchStartX === null) return;
    const currentX = event.changedTouches[0]?.clientX;
    const diffX = touchStartX - currentX;

    if (diffX > 50) {
      handlePrev();
    } else if (diffX < -50) {
      handleNext();
    }
    setTouchStartX(null);
  };

  const isRtl = i18n.dir() === "rtl";
  const slideWidth = itemsPerSlide > 1 ? 50 : 100;
  
  return (
    <div className={textSliderStyles.carouselWrapper}>
      {openModal && currentAppointment && (
        <Modal onClose={() => setOpenModal(false)}>
          <AppointmentCard
            onClose={() => setOpenModal(false)}
            appointmentData={currentAppointment}
          />
        </Modal>
      )}

      {openVideoModal && currentExercise && (
        <Modal onClose={() => setOpenVideoModal(false)}>
          <VideoModalContent
            exercise={currentExercise}
            onClose={() => setOpenVideoModal(false)}
          />
        </Modal>
      )}

      <div
        className={textSliderStyles.carouselItems}
        style={{ transform: `translateX(${isRtl ? activeIndex * slideWidth : -activeIndex * slideWidth}%)`}}       
      >
        {items.map((item, index: number) => {
          if (typeof item === "string") {
            return (
              <div
                key={index}
                onTouchStart={handleTouchStart}
                onTouchEnd={(e) => handleTouchEnd(e)}
                className={`${textSliderStyles.carouselItem}`}
              >
                <p className={className}>{item}</p>
              </div>
            );
          }

          if ("desc" in item) {
            return (
              <div
                key={index}
                onTouchStart={handleTouchStart}
                onTouchEnd={(e) => handleTouchEnd(e)}
                className={`${textSliderStyles.carouselItem}`}
              >
                <div className={myDayStyles.updatesContainer}>
                  <p className={`${className} ${myDayStyles.updateTitle}`}>
                    {replaceVariables(
                      `${t("HELLO")} @NAME:`,
                      user
                    )}
                  </p>
                  <p className={className}>{item.title}</p>
                  <p className={`${className} ${myDayStyles.desc}`}>
                    {item.desc}
                  </p>
                </div>
              </div>
            );
          } else if ("text" in item) {
            // Handle displayUpdates format with text property
            return (
              <div
                key={index}
                onTouchStart={handleTouchStart}
                onTouchEnd={(e) => handleTouchEnd(e)}
                className={`${textSliderStyles.carouselItem}`}
              >
                <div className={myDayStyles.updatesContainer}>
                  <p className={`${className} ${myDayStyles.updateTitle}`}>
                    {replaceVariables(
                      `${t("HELLO")} @NAME:`,
                      user
                    )}
                  </p>
                  <p className={`${className} ${myDayStyles.desc}`}>
                    {parseLinksInText(item.text)}
                  </p>
                </div>
              </div>
            );
          } else if ("title" in item && "link" in item) {
            return (
              <li
                key={index}
                onTouchStart={handleTouchStart}
                onTouchEnd={(e) => handleTouchEnd(e)}
                className={`${textSliderStyles.carouselItem} ${textSliderStyles.carouselItemVideo} ${myDayStyles.myDay__sectionCard} ${myDayStyles.myDay__sectionCard_centered}`}
              >
                <div className={myDayStyles.myDay__sectionContent}>
                  <VideoThumbnail 
                    exercise={item as TExercise}
                    onClick={() => openExerciseModal(item as TExercise)}
                  />
                </div>
              </li>
            );
          } else {
            return (
              <li
                key={index}
                style={{ flex: '0 0 50%', maxWidth: '50%' }}
                onTouchStart={handleTouchStart}
                onTouchEnd={(e) => handleTouchEnd(e)}
                onClick={() => {
                  setCurrentAppointment(item);
                  setOpenModal(true);
                }}
                className={`${textSliderStyles.carouselItem} ${myDayStyles.myDay__sectionCard} ${myDayStyles.myDay__sectionCard_line} ${myDayStyles.myDay__sectionCardAppointments}`}
              >
                <div>
                  <p className={myDayStyles.myDay__sectionText}>
                    {item.appointments.medias
                      && ` ${t("APPOINTMENT_WITH")} ${
                          item.appointments.medias.name
                        }`}
                        </p>
                  <p className={myDayStyles.myDay__sectionText}>
                    
                      {`${t("APPOINTMENT_WITH_DOCTOR")} ${item.therapistName}`}
                  </p>
                  <p className={myDayStyles.myDay__sectionText}>
                    {formatAppointmentDate(item.appointments.appointmentDate)}
                  </p>
                  <p className={myDayStyles.myDay__sectionText}>
                    {item.appointments.endTime?.slice(0, 5) ?? "10:00"} -{" "}
                    {item.appointments.startTime?.slice(0, 5) ?? "09:00"}
                  </p>
                </div>
              </li>
            );
          }
        })}
      </div>
    </div>
  );
}

export default Slider;
